import { useCallback, useMemo, useRef } from 'react';
import { JOB_ORDER_PROFILE_CONFIG, PROFILE_REQUESTS } from 'features/inventory-profiles/constants';
import { buildProfileEndpoint } from 'features/inventory-profiles/utils';
import { useCreateProfileView } from 'hooks/createProfileView';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import useFetchCatalogs from 'hooks/useFetchCatalogs';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import API from 'services/API';
import { RequestStatus, UIStatus } from 'UI/constants/status';
import { useFetchDataSheet } from 'UI/pages/EditDataSheet/hooks';

const INITIAL_JOB_ORDER = {};

/**
 * Job order profile, its data sheet and the catalogs of its company. As in V1, the job order
 * carries the status chosen in its data sheet, and the Overview waits for both.
 * Tracks the view once the job order has loaded.
 * @param {number|string} id Job order id
 * @returns {{
 *   jobOrder: Object,
 *   dataSheet: Object | undefined,
 *   catalogs: Object | null,
 *   error: { request: string, statusCode?: number } | null,
 *   isAlreadyAccountable: boolean,
 *   isLoading: boolean,
 *   isOverviewLoading: boolean,
 *   refresh: () => void,
 *   reload: () => void
 * }}
 */
export const useJobOrderProfile = id => {
  const [user] = useUserContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const latestRequestRef = useRef(null);

  const jobOrderEndpoint = buildProfileEndpoint({
    entity: JOB_ORDER_PROFILE_CONFIG,
    id,
    isEnabled: checkIfFeatureFlagEnabled
  });

  const loadProfile = useCallback(() => {
    const profileRequest = API.get(jobOrderEndpoint, { apiVersion: 2 });
    latestRequestRef.current = profileRequest;

    const resolveWithLatestRequest = () => latestRequestRef.current;

    return profileRequest.then(resolveWithLatestRequest, resolveWithLatestRequest);
  }, [jobOrderEndpoint]);

  const { state, refreshData } = useFetchWithStatusV2({
    promise: id ? loadProfile : undefined,
    initialState: INITIAL_JOB_ORDER
  });

  const { status, results, responseStatusCode } = state;
  const isLoading = status === UIStatus.Loading;
  const profile = isLoading ? INITIAL_JOB_ORDER : results ?? INITIAL_JOB_ORDER;

  useCreateProfileView({ id: profile.id, endpoint: JOB_ORDER_PROFILE_CONFIG.trackingUrl });

  const dataSheetResponse = useFetchDataSheet(profile.id);
  const catalogs = useFetchCatalogs({ companyId: profile.company?.id });
  const isOverviewLoading =
    isLoading ||
    dataSheetResponse.status === RequestStatus.Loading ||
    catalogs.uiState === UIStatus.Loading;

  const dataSheetStatusId = dataSheetResponse.dataSheet?.data?.section9?.jobOrderStatus;
  const jobOrder = useMemo(
    () =>
      profile.id
        ? {
            ...profile,
            status:
              catalogs.catalogs?.jobOrderStatuses?.data?.find(
                item => item.id === dataSheetStatusId
              ) ?? profile.status
          }
        : profile,
    [profile, catalogs.catalogs, dataSheetStatusId]
  );

  return {
    jobOrder,
    dataSheet: dataSheetResponse.dataSheet,
    catalogs: catalogs.catalogs,
    error:
      status === UIStatus.Error
        ? { request: PROFILE_REQUESTS.Profile, statusCode: responseStatusCode }
        : null,
    isAlreadyAccountable: jobOrder.recruiter?.id === user.id,
    isLoading,
    isOverviewLoading,
    refresh: () => refreshData(false),
    reload: () => refreshData()
  };
};
